import { useState } from "react";
import { useEventId } from "./useEvent";

const safeDefault: object = {
  pin: undefined,
  uid: undefined,
  orderUuid: undefined,
  orderId: undefined,
};

export interface AppData {
  team: Team;
  uid?: string;
  pin?: string;
  orderUuid?: string;
  orderId?: string;
  stripeSessionId?: string;
  authToken?: string;
  isAdmin?: boolean;
  isUploader?: boolean;
  hideUpgradePopup?: boolean;
  redirectBackUrl?: string;
  deeplink?: string; // event-relative path (e.g. "/challenges/xyz") to return to after joining a team
  persistedScrollPosition?: number;
  consent?: boolean;
  hasSeenAbout?: boolean;
}

export const BASE_STORAGE_PATH = "camdeed-app-v1.1";

export const usePersistedState = (): [AppData, (AppData: AppData, eventId?: string) => void] => {
  const initialEventId = useEventId();
  const getStorageKey = (eventId?) => `${BASE_STORAGE_PATH}-${eventId ?? initialEventId}`;

  const isBrowser = typeof window !== "undefined";

  // Function to check if localStorage is available
  const isLocalStorageAvailable = () => {
    if (typeof window.localStorage === "undefined" || window.localStorage === null) {
      console.error(`localStorage is not available! Event ID: ${initialEventId}`);
      return false;
    }
    try {
      const testKey = "__test__";
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  };

  const isSessionStorageAvailable = () => {
    if (typeof window.sessionStorage === "undefined" || window.sessionStorage === null) {
      console.error(`sessionStorage is not available! Event ID: ${initialEventId}`);
      return false;
    }

    try {
      const testKey = "__test__";
      window.sessionStorage.setItem(testKey, testKey);
      window.sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  };

  const canUseLocalStorage = isLocalStorageAvailable();
  const canUseSessionStorage = isSessionStorageAvailable();

  // We always use React's useState as the base, but we sync with storage when available
  const [storedValue, setStoredValue] = useState(() => {
    // Only try to read from storage in browser environment
    if (!isBrowser) {
      console.error(`Not in browser environment, in usePersistedState()! Event ID: ${initialEventId}`);
      return safeDefault;
    }

    // Try to get initial value from localStorage
    if (canUseLocalStorage) {
      try {
        const item = window.localStorage.getItem(getStorageKey());
        return item ? JSON.parse(item) : safeDefault;
      } catch (error) {
        console.error("Error reading localStorage:", error);
        return safeDefault;
      }
    }

    // Try sessionStorage as fallback
    if (canUseSessionStorage) {
      try {
        const item = window.sessionStorage.getItem(getStorageKey());
        return item ? JSON.parse(item) : safeDefault;
      } catch (error) {
        console.error("Error reading sessionStorage:", error);
        return safeDefault;
      }
    }

    // If no storage is available, just use regular React state
    // The state will work normally but won't persist between page reloads
    console.error(`No storage available, in usePersistedState()! Event ID: ${initialEventId}`);
    return safeDefault;
  });

  const setValue = (value, eventId = undefined) => {
    try {
      // Always update React state regardless of storage availability
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      // Only try to sync with storage in browser environment
      if (!isBrowser) {
        console.error(`Not in browser environment, in setValue()! Event ID: ${eventId}`);
        return;
      }

      // Try to sync with storage if available
      if (canUseLocalStorage) {
        try {
          window.localStorage.setItem(getStorageKey(eventId), JSON.stringify(valueToStore));
        } catch (error) {
          console.error("Error writing to localStorage:", error);
        }
      } else if (canUseSessionStorage) {
        try {
          window.sessionStorage.setItem(getStorageKey(eventId), JSON.stringify(valueToStore));
        } catch (error) {
          console.error("Error writing to sessionStorage:", error);
        }
      } else {
        console.error(`No storage available, in setValue()! Event ID: ${eventId}`);
      }
    } catch (error) {
      console.error("Error in setValue:", error);
    }
  };

  return [storedValue, setValue];
};
